import { useContext } from 'react';

import {
  LocalizationContext,
  type LocalizationContextValue,
} from './LocalizationContext';

/**
 * Reads the resolved preferences published by `LocalizationProvider`.
 *
 * Feature code will typically use higher-level hooks built on this (`useFormat`,
 * `useLocalizationPreferences`). This is the primitive they sit on.
 *
 * @throws if no provider is above the caller. A missing provider is a wiring bug, not a data
 * problem: unknown preference codes can be dropped, but with no provider every value below would be
 * silently wrong.
 */
export const useLocalizationContext = (): LocalizationContextValue => {
  const value = useContext(LocalizationContext);

  if (!value) {
    throw new Error(
      'useLocalizationContext was called with no <LocalizationProvider> above it. ' +
        'Mount the provider once near the root of the app shell. If this component renders inside a ' +
        'federated remote, the likely cause is that @brightdrop/localization-core is not declared as a ' +
        "shared singleton in that app's vite.config.mjs, so the remote is reading its own copy of the " +
        "context instead of the host's."
    );
  }

  return value;
};
